import React, { useEffect, useState, useRef, createElement } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { trackPixel, trackHighIntentVisitor, trackFormOpened } from './lib/pixel';
import { useEngagementTracking } from './lib/useEngagementTracking';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { ScrollToTop } from './components/layout/ScrollToTop';
import { InquiryModal } from './components/forms/InquiryModal';
import { HomePage } from './pages/HomePage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { ApplicationsPage } from './pages/ApplicationsPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { LegalPage } from './pages/LegalPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { ShippingPaymentPage } from './pages/ShippingPaymentPage';
import { ReturnsRefundsPage } from './pages/ReturnsRefundsPage';
import { ServicePage } from './pages/ServicePage';
import { GalleryPage } from './pages/GalleryPage';
import { FAQPage } from './pages/FAQPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { LanguageProvider } from './lib/i18n';
import { WhatsAppButton } from './components/layout/WhatsAppButton';
import { CookieBanner } from './components/layout/CookieBanner';
import { BackToTop } from './components/layout/BackToTop';
// Mounts engagement tracking (SPA PageView, scroll depth, session/page-based
// HighIntent + Engaged custom events). Must live inside the Router.
function EngagementTracker() {
  useEngagementTracking();
  return null;
}
export function App() {
  useEffect(() => {
    const title = 'D-TECH | Električna kolica i sistemi za transport tereta';
    const description = 'Profesionalna rešenja za vertikalni transport tereta. Brzi transport. Veća produktivnost. Veća bezbednost.';
    const ogImage = 'https://cdn.magicpatterns.com/uploads/mrmC3EPvZ6yt8zBvajm1cp/LOGO_FINAL.jpg';
    const siteUrl = 'https://dtech.rs';
    document.title = title;
    const upsertMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    // Facebook domain verification (injected into <head> on every page)
    upsertMeta('name', 'facebook-domain-verification', '7hsvp5ojixf82to13asu9schnyqfi8');
    // Standard SEO
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', 'električni transporter, transport tereta, D-TECH, vertikalni transport, SCA, SCB, RAPTOR, SCC, transport uz stepenice, industrijski transporteri');
    upsertMeta('name', 'robots', 'index, follow');
    // Open Graph (Viber, WhatsApp, Facebook, LinkedIn previews)
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:url', siteUrl);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:locale', 'sr_RS');
    upsertMeta('property', 'og:site_name', 'D-TECH Solutions');
    // Twitter Card
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', ogImage);
    // Favicon (browser tab icon)
    const faviconUrl = 'https://cdn.magicpatterns.com/uploads/cPqTf1LJZhBwCZDLYdDrYg/LOOG_GMAIL.jpg';
    const upsertLink = (rel: string, href: string) => {
      let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };
    upsertLink('icon', faviconUrl);
    upsertLink('shortcut icon', faviconUrl);
    upsertLink('apple-touch-icon', faviconUrl);
    const returnPolicy = {
      '@type': 'MerchantReturnPolicy',
      url: 'https://dtech.rs/reklamacije',
      applicableCountry: 'RS',
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/ReturnShippingFees'
    };
    const shippingDetails = {
      '@type': 'OfferShippingDetails',
      shippingSettingsLink: 'https://dtech.rs/isporuka-i-placanje',
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: 'RS'
      }
    };
    // JSON-LD Schema
    const schemaData = {
      '@context': 'https://schema.org',
      '@graph': [{
        '@type': 'Organization',
        '@id': 'https://dtech.rs/#organization',
        name: 'D-TECH Solutions',
        url: 'https://dtech.rs',
        logo: 'https://cdn.magicpatterns.com/uploads/mrmC3EPvZ6yt8zBvajm1cp/LOGO_FINAL.jpg',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+381-65-331-2811',
          contactType: 'customer service',
          email: 'info@dtech.rs',
          availableLanguage: ['Serbian', 'English']
        },
        sameAs: ['https://www.instagram.com/dtech.rs', 'https://www.facebook.com/profile.php?id=61589561484063', 'https://www.tiktok.com/@dtech_rs', 'https://www.linkedin.com/company/115805045/']
      }, {
        '@type': 'Product',
        name: 'D-TECH SCA',
        image: 'https://cdn.magicpatterns.com/uploads/rf5NrgHygwqJiVRF6Ubzv8/SCA_za_sajt.jpg',
        description: 'Industrijska verzija za teške uslove rada. Stabilna na grubim stepenicama i podlogama zahvaljujući dva pogonska točka i zupčastom mehanizmu.',
        brand: {
          '@type': 'Brand',
          name: 'D-TECH'
        },
        mpn: 'SCA-STANDARD',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: '1790',
          availability: 'https://schema.org/PreOrder',
          url: 'https://dtech.rs/products/sca',
          hasMerchantReturnPolicy: returnPolicy,
          shippingDetails: shippingDetails
        }
      }, {
        '@type': 'Product',
        name: 'D-TECH SCB (STANDARD)',
        image: 'https://cdn.magicpatterns.com/uploads/2JFxrQoz9bUYCTY3p3fnnv/DTECH_STANDARD.jpg',
        description: 'Osnovna konfiguracija za svakodnevnu profesionalnu upotrebu. Pouzdan transport tereta uz stepenice.',
        brand: {
          '@type': 'Brand',
          name: 'D-TECH'
        },
        mpn: 'SCB-STANDARD',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: '1990',
          availability: 'https://schema.org/PreOrder',
          url: 'https://dtech.rs/products/standard',
          hasMerchantReturnPolicy: returnPolicy,
          shippingDetails: shippingDetails
        }
      }, {
        '@type': 'Product',
        name: 'D-TECH RAPTOR (HEAVY DUTY)',
        image: 'https://cdn.magicpatterns.com/uploads/qLkakcP4DrV2Dp874ZqZ5Q/RPT.jpg',
        description: 'Premium konfiguracija sa zaštitnom bojom otpornom na habanje i dve baterije za produženi rad.',
        brand: {
          '@type': 'Brand',
          name: 'D-TECH'
        },
        mpn: 'RAPTOR',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: '2190',
          availability: 'https://schema.org/PreOrder',
          url: 'https://dtech.rs/products/raptor',
          hasMerchantReturnPolicy: returnPolicy,
          shippingDetails: shippingDetails
        }
      }, {
        '@type': 'Product',
        name: 'D-TECH SCC (PRO)',
        image: 'https://cdn.magicpatterns.com/uploads/hggMGv5HaYSpxFwXnwmnHo/SCC_PRO.png',
        description: 'Unapređena mašina za penjanje uz stepenice sa malim točkovima i hidrauličnom/elektromagnetnom potpornom šipkom.',
        brand: {
          '@type': 'Brand',
          name: 'D-TECH'
        },
        mpn: 'SCC-PRO',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: '2490',
          availability: 'https://schema.org/InStock',
          url: 'https://dtech.rs/products/scc-pro',
          hasMerchantReturnPolicy: returnPolicy,
          shippingDetails: shippingDetails
        }
      }]
    };
    let scriptEl = document.head.querySelector<HTMLScriptElement>('script[type="application/ld+json"]');
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptEl);
    }
    scriptEl.textContent = JSON.stringify(schemaData);
    // Google Analytics (GA4) - G-5F0246P87J
    const GA_ID = 'G-5F0246P87J';
    if (!document.head.querySelector(`script[data-ga-loader="true"]`)) {
      const gaLoader = document.createElement('script');
      gaLoader.async = true;
      gaLoader.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      gaLoader.setAttribute('data-ga-loader', 'true');
      document.head.appendChild(gaLoader);
    }
    if (!document.head.querySelector(`script[data-ga-init="true"]`)) {
      const gaInit = document.createElement('script');
      gaInit.setAttribute('data-ga-init', 'true');
      gaInit.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', '${GA_ID}');
      `;
      document.head.appendChild(gaInit);
    }
    // Meta Pixel (Facebook) - 2945020945847469
    const FB_PIXEL_ID = '2945020945847469';
    if (!document.head.querySelector(`script[data-fb-pixel="true"]`)) {
      const fbScript = document.createElement('script');
      fbScript.setAttribute('data-fb-pixel', 'true');
      fbScript.textContent = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${FB_PIXEL_ID}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(fbScript);
      // noscript fallback pixel
      const fbNoscript = document.createElement('noscript');
      fbNoscript.setAttribute('data-fb-pixel-noscript', 'true');
      const fbImg = document.createElement('img');
      fbImg.height = 1;
      fbImg.width = 1;
      fbImg.style.display = 'none';
      fbImg.src = `https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`;
      fbNoscript.appendChild(fbImg);
      document.body.appendChild(fbNoscript);
    }
    // Microsoft Clarity - x7jr8gyk2a
    // Loads globally; natively handles SPA navigation, session recordings,
    // heatmaps, click/scroll tracking, and rage/dead-click detection.
    const CLARITY_ID = 'x7jr8gyk2a';
    if (!document.head.querySelector(`script[data-clarity="true"]`)) {
      const clarityScript = document.createElement('script');
      clarityScript.type = 'text/javascript';
      clarityScript.setAttribute('data-clarity', 'true');
      clarityScript.textContent = `
        (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "${CLARITY_ID}");
      `;
      document.head.appendChild(clarityScript);
    }
  }, []);
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
  const [preselectedProductId, setPreselectedProductId] = useState<string | undefined>();
  const handleRequestQuote = (productId?: string) => {
    // "Zatražite ponudu" CTA — opening the inquiry flow is a strong intent signal.
    trackPixel('Contact', {
      content_name: 'Zatrazite ponudu',
      button_text: 'Zatrazite ponudu'
    });
    trackFormOpened('inquiry_modal');
    trackHighIntentVisitor('opened inquiry form');
    setPreselectedProductId(productId);
    setIsInquiryModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsInquiryModalOpen(false);
    setPreselectedProductId(undefined);
  };
  return <LanguageProvider data-id="element-0" data-mp="1kgfddupycew6">
      <BrowserRouter data-id="element-1" data-mp="1ffjclq0wd8uz">
        <ScrollToTop data-id="element-2" data-mp="1fezv2af5smhh" />
        <EngagementTracker data-id="element-3" data-mp="1fgkrk5l1afhz" />
        <div className="min-h-screen flex flex-col bg-dark-bg" data-id="element-4" data-mp="1fhci31zrxn1e">
          <Header onRequestQuote={() => handleRequestQuote()} data-id="element-5" data-mp="rqgmruw409pe" />
          <Routes data-id="element-6" data-mp="mquapos0ozi6">
            <Route path="/" element={<HomePage onRequestQuote={() => handleRequestQuote()} data-id="element-8" data-mp="msykuogieuqw" />} data-id="element-7" data-mp="mrnimt2ppv1w" />
            <Route path="/products" element={<ProductsPage onRequestQuote={() => handleRequestQuote()} data-id="element-10" data-mp="wnyz7v9c5725" />} data-id="element-9" data-mp="11p61xfwmfg1y" />
            <Route path="/products/:slug" element={<ProductDetailPage data-id="element-12" data-mp="2d4upo9hp8j3t" />} data-id="element-11" data-mp="2d1gov5ooohyh" />
            <Route path="/applications" element={<ApplicationsPage data-id="element-14" data-mp="283nlsliog42f" />} data-id="element-13" data-mp="284q8sicvpmd2" />
            <Route path="/about" element={<AboutPage data-id="element-16" data-mp="hnv32jw2fnbb" />} data-id="element-15" data-mp="hn35j3iwrfie" />
            <Route path="/contact" element={<ContactPage data-id="element-18" data-mp="mlwld2lo3ilq" />} data-id="element-17" data-mp="hoo0gu94x0gs" />
            <Route path="/legal" element={<LegalPage data-id="element-20" data-mp="mmg24fzl00y6" />} data-id="element-19" data-mp="ml4q5svzfmvz" />
            <Route path="/politika-privatnosti" element={<PrivacyPolicyPage data-id="element-22" data-mp="qlyf8pl5v7qg" />} data-id="element-21" data-mp="mnr6agwxvapt" />
            <Route path="/uslovi-koriscenja" element={<TermsOfServicePage data-id="element-24" data-mp="vo5upp5795xj" />} data-id="element-23" data-mp="vmcizvfqbvio" />
            <Route path="/isporuka-i-placanje" element={<ShippingPaymentPage data-id="element-26" data-mp="10lffuslk2vfk" />} data-id="element-25" data-mp="vk8bl3imgedn" />
            <Route path="/reklamacije" element={<ReturnsRefundsPage data-id="element-28" data-mp="15jh8a44vg73p" />} data-id="element-27" data-mp="15k8u4n8o2qgs" />
            <Route path="/servis" element={<ServicePage data-id="element-30" data-mp="1aaz3ebk7rdgr" />} data-id="element-29" data-mp="15ifptelj0iby" />
            <Route path="/gallery" element={<GalleryPage data-id="element-32" data-mp="1aduyls4ofo1i" />} data-id="element-31" data-mp="1ac0ol8uy1gc7" />
            <Route path="/faq" element={<FAQPage data-id="element-34" data-mp="1ebsoyya6og6h" />} data-id="element-33" data-mp="1edluga928ucw" />
            <Route path="*" element={<NotFoundPage data-id="element-36" data-mp="1jc704p73068q" />} data-id="element-35" data-mp="1ef6qy5exqnde" />
          </Routes>
          <Footer data-id="element-37" data-mp="1jdhyg9hrclsg" />
          <InquiryModal isOpen={isInquiryModalOpen} onClose={handleCloseModal} preselectedProductId={preselectedProductId} data-id="element-38" data-mp="1je039uihk8bz" />
          <WhatsAppButton data-id="element-39" data-mp="1yfzy5q0hm68s" />
          <BackToTop data-id="element-40" data-mp="1ygi0n4dqtgp5" />
          <CookieBanner data-id="element-41" data-mp="1tg3szng208rn" />
        </div>
      </BrowserRouter>
    </LanguageProvider>;
}